/**
 * One idempotency key per intent (audit finding W-1, the owner, 2026-10-24).
 *
 * The server recognises a repeat only by its key: the same key twice is one fact (the same payment id comes back), a new key is a
 * new fact. A key built INSIDE the call (`crypto.randomUUID()` in the mutation) is new on every attempt, so a retry after a timeout,
 * or a second press that slips past the busy flag, would post a second payment. A key held for the whole intent fixes that.
 *
 * The key here is derived from a per-intent salt and the exact payload being sent:
 *   · the same payload again (a retry, a double press)            → the same key, so the server answers with the first result;
 *   · a different payload (the person changed the amount first)   → a different key, so it is a new fact and is never refused as
 *                                                                    "same key, different payload" (RTC_IDEMPOTENCY_MISMATCH);
 *   · after a success, `renew()` gives the next intent a new salt  → two separate, identical payments are never merged into one.
 */
import { useRef } from 'react';

/** Stable JSON: object keys sorted, so the same payload always gives the same text whatever order it was built in. */
export function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => `${JSON.stringify(k)}:${canonical(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(value) ?? 'null';
}

/** A UUID (version-5 layout) from the salt and the payload. Pure apart from the digest, so it is testable. */
export async function idempotencyKey(salt: string, payload: unknown): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(`${salt}|${canonical(payload)}`));
  const bytes = new Uint8Array(digest).slice(0, 16);
  bytes[6] = (bytes[6]! & 0x0f) | 0x50;
  bytes[8] = (bytes[8]! & 0x3f) | 0x80;
  const h = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20, 32)}`;
}

export function useIdempotencyKey() {
  const salt = useRef<string>(crypto.randomUUID());
  return {
    /** The key for this payload within the current intent. Call it inside the mutation, with the arguments about to be sent. */
    keyFor: (payload: unknown) => idempotencyKey(salt.current, payload),
    /** The intent succeeded: the next one starts afresh. */
    renew: () => { salt.current = crypto.randomUUID(); },
  };
}
